// ============================================================
// DockerCapstone.js
// ============================================================
// Junior-dev Notes:
// - Fix: Escaped '<' character using HTML entity &lt; to prevent JSX parsing errors.
// - Concept: The "Zero to Production" Workflow.
// - Stack: React (Vite) + Express (Node) + PostgreSQL.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* Icons */
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import HubIcon from "@mui/icons-material/Hub";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import CodeIcon from "@mui/icons-material/Code";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple
const FINLAND_BLUE = "#003580";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.7)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const BlueprintCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    height: "100%",
    transition: "0.3s ease",
    "&:hover": {
        borderColor: DOCKER_BLUE,
        backgroundColor: "rgba(229, 201, 95, 0.05)",
        transform: "translateY(-5px)"
    }
}));

const CodeWindow = styled(Box)(({ theme }) => ({
    backgroundColor: "#011627",
    padding: theme.spacing(3),
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    fontSize: "0.8rem",
    color: "#82AAFF",
    boxShadow: "0 10px 30px rgba(0,0,0,0.5)"
}));

const FinnishBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "4px",
    border: `1px solid rgba(255,255,255,0.1)`,
    backgroundColor: "rgba(255,255,255,0.03)",
    marginTop: "20px"
});

export default function DockerCapstone() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Capstone Project · Module 17"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Full-Stack <span style={{ color: DOCKER_BLUE }}>Containerization</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        It's time to build. We are going to dockerize a 3-tier application,
                        implement volume persistence for data, and use a custom bridge network
                        for secure service communication.
                    </Typography>

                    <FinnishBadge>
                        <Box sx={{ width: 18, height: 12, position: 'relative', bgcolor: '#fff' }}>
                            <Box sx={{ position: 'absolute', top: '40%', width: '100%', height: '20%', bgcolor: FINLAND_BLUE }} />
                            <Box sx={{ position: 'absolute', left: '30%', width: '15%', height: '100%', bgcolor: FINLAND_BLUE }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 1, color: "#fff", opacity: 0.8 }}>
                            FINNISH ENGINEERING STANDARDS
                        </Typography>
                    </FinnishBadge>
                </Box>

                {/* 2. THE ARCHITECTURE BLUEPRINT */}
                <Typography variant="h4" sx={{ mb: 6, fontWeight: 800, textAlign: 'center' }}>
                    Project <span style={{ color: DOCKER_BLUE }}>Architecture</span>
                </Typography>



                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={4}>
                        <BlueprintCard>
                            <CodeIcon sx={{ color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>Frontend Layer</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                React/Vite app. Multi-stage build using Nginx to serve static files.
                                Optimized for &lt; 20MB.
                            </Typography>
                        </BlueprintCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <BlueprintCard>
                            <HubIcon sx={{ color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>API Layer</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                Node.js Express server. Communicates with the Database via internal
                                DNS (Service Discovery).
                            </Typography>
                        </BlueprintCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <BlueprintCard>
                            <StorageIcon sx={{ color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>Data Layer</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                PostgreSQL instance. Persistent storage via Named Volumes to
                                ensure data survives restarts.
                            </Typography>
                        </BlueprintCard>
                    </Grid>
                </Grid>

                {/* 3. THE COMPOSE BLUEPRINT */}
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 800 }}>
                    The <span style={{ color: DOCKER_BLUE }}>Orchestration</span> File
                </Typography>

                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={8}>
                        <CodeWindow>
                            <Typography sx={{ color: "#C792EA" }}>services:</Typography>
                            <Typography sx={{ ml: 2, color: "#82AAFF" }}>frontend:</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>build: ./client</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>ports: ["80:80"]</Typography>

                            <Typography sx={{ ml: 2, mt: 1, color: "#82AAFF" }}>backend:</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>build: ./server</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>env_file: .env</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>depends_on: [db]</Typography>

                            <Typography sx={{ ml: 2, mt: 1, color: "#82AAFF" }}>db:</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>image: postgres:alpine</Typography>
                            <Typography sx={{ ml: 4, color: BRAND.muted }}>volumes: ["pgdata:/var/lib/data"]</Typography>
                        </CodeWindow>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3} sx={{ height: '100%', justifyContent: 'center' }}>
                            <Box sx={{ p: 3, bgcolor: "rgba(229, 201, 95, 0.05)", borderRadius: 2, border: `1px solid ${BRAND.border}` }}>
                                <SettingsEthernetIcon sx={{ color: DOCKER_BLUE, mb: 1 }} />
                                <Typography variant="subtitle2" fontWeight={800}>Custom Network</Typography>
                                <Typography variant="caption" color={BRAND.muted}>
                                    Containers talk via service names (e.g., http://db:5432).
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3, bgcolor: "rgba(0, 237, 100, 0.05)", borderRadius: 2, border: "1px solid rgba(0, 237, 100, 0.2)" }}>
                                <StorageIcon sx={{ color: "#00ED64", mb: 1 }} />
                                <Typography variant="subtitle2" fontWeight={800}>Volume Persistence</Typography>
                                <Typography variant="caption" color={BRAND.muted}>
                                    Named volumes protect your SQL data from container deletions.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* 4. CAPSTONE CHALLENGES */}
                <Paper sx={{ p: 5, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                    <Typography variant="h5" fontWeight={900} sx={{ color: DOCKER_BLUE, mb: 4, textAlign: 'center' }}>
                        Your <span style={{ color: TEXT_WHITE }}>Final Mission</span>
                    </Typography>
                    <Grid container spacing={4}>
                        {[
                            { title: "Multi-Stage Build", text: "Create a Dockerfile for the React app that uses Node to build and Nginx to serve." },
                            { title: "Health Checks", text: "Configure the API to wait until the Database is 'Healthy' before starting up." },
                            { title: "Environment Injection", text: "Use a .env file to pass the DB credentials securely into the container." },
                            { title: "Optimization", text: "Ensure your final images are under 100MB and use .dockerignore for all layers." }
                        ].map((task, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Stack direction="row" spacing={2}>
                                    <AssignmentTurnedInIcon sx={{ color: "#00ED64" }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={800}>{task.title}</Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.muted }}>{task.text}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <RocketLaunchIcon sx={{ fontSize: 60, color: DOCKER_BLUE, mb: 2 }} />
                    <Typography variant="h4" sx={{ mb: 4, fontWeight: 900 }}>Knowledge Solidified.</Typography>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 6, maxWidth: 600, mx: 'auto' }}>
                        You have traveled from basic 'docker run' commands to orchestrating complex,
                        production-grade SaaS ecosystems. You are now ready for the Official Certification.
                    </Typography>
                    <Link to="/quizzes" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 10,
                                py: 2.5,
                                borderRadius: "4px",
                                boxShadow: `0 8px 24px rgba(229, 201, 95, 0.4)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Final Assessment
                        </Button>
                    </Link>
                </Box>

            </Container>
        </PageWrapper>
    );
}
